import type { Question } from '@mentimeter/http-clients';
import { usePresentationContext } from '../../legacy-context/presentationContext';
import type { ModeT } from '../../types';
import type { VisualizationState } from '../../utils/getVisualizationState';
import { ScreenshotViewDataHandler } from './presentation/ScreenshotView';
import { StartVotingView } from './presentation/StartVotingView';
import { ShowGroupDetailViewProvider } from './presentation/utils/useShowGroupDetailView';
import { GroupsWithVotesDataHandler } from './presentation/grouping/groups-with-votes-view/GroupsWithVotesDataHandler';
import { GroupsWithoutVotesDataHandler } from './presentation/grouping/groups-without-votes-view/GroupsWithoutVotesDataHandler';
import { EditorEmptyState } from './presentation/EditorEmptyState';
import type { OpenEndedResponse } from './presentation/types';
import { ResponsesOrFocusedView } from './presentation/responses-view/ResponsesOrFocusedView';
import { ViewContainer } from './presentation/ui/ViewContainer';

interface PresentationModuleProps {
  visualizationState: VisualizationState;
  mode: ModeT;
  shouldHideResults: boolean;
  responses: OpenEndedResponse[];
  numberOfVoters: number;
  numberOfColumns: 2 | 3;
  isUpvotingEnabled: Question['voting_on_answers'];
  shouldShowGroups: boolean;
}
export const PresentationModule = ({
  visualizationState,
  mode,
  shouldHideResults,
  responses,
  numberOfVoters,
  numberOfColumns,
  isUpvotingEnabled,
  shouldShowGroups,
}: PresentationModuleProps) => {
  const { InteractiveCanvasComponents } = usePresentationContext();

  if (mode === 'screenshot') {
    return (
      <ScreenshotViewDataHandler
        responses={responses}
        votingOnAnswers={isUpvotingEnabled}
      />
    );
  }

  if (visualizationState === 'start-voting') {
    return <StartVotingView numberOfVoters={numberOfVoters} />;
  }

  if (shouldShowGroups) {
    return (
      <ViewContainer>
        <ShowGroupDetailViewProvider>
          {visualizationState === 'view-answers' ? (
            <GroupsWithoutVotesDataHandler />
          ) : (
            <GroupsWithVotesDataHandler />
          )}
        </ShowGroupDetailViewProvider>
      </ViewContainer>
    );
  }

  return (
    <ViewContainer>
      {visualizationState === 'editor-empty' ? (
        <EditorEmptyState
          numberOfColumns={numberOfColumns}
          CanvasVisualizationArea={
            InteractiveCanvasComponents?.CanvasVisualizationArea
          }
        />
      ) : (
        <ResponsesOrFocusedView
          responses={responses}
          CanvasVisualizationArea={
            InteractiveCanvasComponents?.CanvasVisualizationArea
          }
          shouldHideResults={shouldHideResults}
          numberOfColumns={numberOfColumns}
          visualizationState={visualizationState}
        />
      )}
    </ViewContainer>
  );
};
